import { cn } from '@/lib/utils';
import { PluginConfigData, Section } from '@/types/plugin-config';
import { usePluginConfig, useTranslation } from '@tagadapay/plugin-sdk/v2';
import { DynamicIcon } from './DynamicIcon';

interface SectionIconProps {
  section: Section;
  index: number;
}

/** Resolve section icon: use DynamicIcon (full icon map) or image URL */
function SectionIcon({ section, index }: SectionIconProps) {
  if (!section.useIconUrl && section.icon) {
    return (
      <div
        className="size-10 flex-shrink-0 text-gray-700"
        editor-id={`config.sections.items.${index}.icon`}
      >
        <DynamicIcon name={section.icon} className="size-10 flex-shrink-0 text-gray-700" />
      </div>
    );
  }

  const size = section.size ? `${section.size}px` : '60px';
  if (section.useIconUrl && section.iconUrl) {
    return (
      <img
        editor-id={`config.sections.items.${index}.iconUrl`}
        src={section.iconUrl}
        alt=""
        className="shrink-0 object-contain object-left"
        style={{
          width: size,
          height: size,
        }}
      />
    );
  }

  return null;
}

interface SectionsProps {
  type?: 'desktop' | 'mobile';
}

function Sections({ type = 'desktop' }: SectionsProps) {
  const { config } = usePluginConfig<PluginConfigData>();
  const { t } = useTranslation();
  const sectionsConfig = config?.sections;
  const sections = sectionsConfig?.items;

  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <div
      className={cn({
        'hidden lg:block': type === 'desktop',
        'block lg:hidden': type === 'mobile',
      })}
    >
      {sections && sections.length > 0 && (
        <div className="mt-6 flex flex-col gap-4 lg:mt-10">
          {sectionsConfig?.title && (
            <h2 className="text-2xl font-bold" editor-id="config.sections.title ">
              {t(sectionsConfig.title)}
            </h2>
          )}
          <div
            className={cn({
              'rounded-lg border-2 border-[var(--border-color)] bg-[var(--background-color)] p-6 shadow-sm': !sectionsConfig?.noBorders,
            })}
            editor-id="config.sections.noBorders"
          >
            <div className="space-y-6">
              {sections.map((section, index) => {
                const title = t(section.titleTrans);
                const description = t(section.descriptionTrans);

                return (
                  <div
                    key={section.id || `section-${index}`}
                    className="flex items-center gap-3"
                    editor-id={`config.sections.items.${index}.useIconUrl`}
                  >
                    <SectionIcon index={index} section={section} />
                    <div className="flex-1">
                      {title && (
                        <h3
                          className="text-base font-semibold leading-tight text-[var(--text-color)]"
                          editor-id={`config.sections.items.${index}.titleTrans`}
                        >
                          {title}
                        </h3>
                      )}
                      {description && (
                        <p
                          className="mt-1.5 text-sm leading-relaxed text-[var(--text-secondary-color)]"
                          editor-id={`config.sections.items.${index}.descriptionTrans`}
                        >
                          {description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {sectionsConfig?.postText && (
            <p className="text-xs tabular-nums" editor-id="config.sections.postText">
              {t(sectionsConfig.postText)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default Sections;
